import { motion, useReducedMotion } from "motion/react"
import type { ShotKind } from "../types"
import type { Box } from "./framing"
import { targetFrame, type Insets } from "./target-frame"

// The eTX viewfinder corners, used as the actual framing tool. They sit as a
// guide frame for the shot; with the smart camera they spring onto the car
// the detector found and turn green when locked. Four independent corners,
// moved by transform only (no layout animation).

export type BracketTone = "guide" | "tracking" | "locked"

const ARM = 30 // px, each corner's arm length
const STROKE = 3.5

// Guide frames (0–1 of the visible frame), inside the space the instruction
// and the controls leave, upright or sideways. Whole car: the target for
// it (target-frame.ts); closer: most of that space; close-ups and the VIN: a
// square in the middle of it.
export function guideBox(kind: ShotKind, screen: { w: number; h: number }, insets: Insets): Box {
  if (kind === "wide") return targetFrame(screen, insets).box
  const { safe } = targetFrame(screen, insets)
  if (kind === "closer") return { x: safe.x + safe.w * 0.04, y: safe.y + safe.h * 0.08, w: safe.w * 0.92, h: safe.h * 0.84 }
  const W = Math.max(1, screen.w)
  const H = Math.max(1, screen.h)
  const side = 0.78 * Math.min(safe.w * W, safe.h * H)
  return {
    x: safe.x + (safe.w - side / W) / 2,
    y: safe.y + (safe.h - side / H) / 2,
    w: side / W,
    h: side / H,
  }
}

const SPRING_TRACK = { type: "spring", stiffness: 300, damping: 30 } as const
const SPRING_LOCK = { type: "spring", stiffness: 400, damping: 14 } as const

export function ViewfinderBrackets({
  box,
  width,
  height,
  tone,
}: {
  box: Box
  width: number
  height: number
  tone: BracketTone
}) {
  const reduce = useReducedMotion()
  const pad = tone === "guide" ? 0 : 10
  const left = box.x * width - pad
  const top = box.y * height - pad
  const right = (box.x + box.w) * width + pad
  const bottom = (box.y + box.h) * height + pad
  const color = tone === "locked" ? "var(--color-signal-on-dark)" : "#ffffff"
  const transition = reduce ? { duration: 0 } : tone === "locked" ? SPRING_LOCK : SPRING_TRACK

  const corners: Array<{ key: string; x: number; y: number; rotate: number }> = [
    { key: "tl", x: left, y: top, rotate: 0 },
    { key: "tr", x: right, y: top, rotate: 90 },
    { key: "br", x: right, y: bottom, rotate: 180 },
    { key: "bl", x: left, y: bottom, rotate: 270 },
  ]

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden data-bracket-tone={tone}>
      {corners.map((c) => (
        <motion.div
          key={c.key}
          className="absolute left-0 top-0"
          initial={false}
          animate={{ x: c.x, y: c.y, rotate: c.rotate }}
          transition={transition}
          style={{ width: 0, height: 0 }}
        >
          <svg
            width={ARM}
            height={ARM}
            viewBox={`0 0 ${ARM} ${ARM}`}
            className="absolute -left-[2px] -top-[2px] overflow-visible drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
          >
            <motion.path
              d={`M ${STROKE / 2} ${ARM} L ${STROKE / 2} ${8} Q ${STROKE / 2} ${STROKE / 2} ${8} ${STROKE / 2} L ${ARM} ${STROKE / 2}`}
              fill="none"
              strokeWidth={STROKE}
              strokeLinecap="round"
              initial={false}
              animate={{ stroke: color, opacity: tone === "guide" ? 0.85 : 1 }}
              transition={{ duration: reduce ? 0 : 0.18 }}
            />
          </svg>
        </motion.div>
      ))}
    </div>
  )
}
